import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useState } from "react";
import { ALL } from "schemas/forms";
import { SetAlertType } from "schemas/functions";

import {
  Template,
  TemplateFolder,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

export type FolderSelection = number | typeof ALL;

interface FolderChipsProps {
  templates: Template[];
  selectedFolderId: FolderSelection;
  setSelectedFolderId: (selection: FolderSelection) => void;
  setAlert: SetAlertType;
}

export default function FolderChips({
  templates,
  selectedFolderId,
  setSelectedFolderId,
  setAlert,
}: FolderChipsProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { folders, fetchFolders, fetchOutreachTemplates } = useContext(
    OutreachTemplatesContext,
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuFolder, setMenuFolder] = useState<TemplateFolder | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<TemplateFolder | null>(
    null,
  );
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const countFor = (selection: FolderSelection) => {
    if (selection === ALL) {
      return templates?.length ?? 0;
    }
    return templates?.filter((t) => t.folderId === selection).length ?? 0;
  };

  const openCreate = () => {
    setEditingFolder(null);
    setName("");
    setEditorOpen(true);
    trackEvent("New Folder Dialog Opened");
  };

  const openRename = (folder: TemplateFolder) => {
    setEditingFolder(folder);
    setName(folder.name);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!currentOrg?.id || !name.trim()) {
      return;
    }
    setSaving(true);
    try {
      const path = editingFolder
        ? `/api/organization/${currentOrg.id}/template-folders/${editingFolder.id}`
        : `/api/organization/${currentOrg.id}/template-folders`;
      await fetcherAuth(
        getToken,
        path,
        editingFolder ? "PUT" : "POST",
        {},
        {
          name: name.trim(),
        },
      );
      await fetchFolders();
      setEditorOpen(false);
      setAlert(editingFolder ? "Folder updated" : "Folder created", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (folder: TemplateFolder) => {
    if (!currentOrg?.id) {
      return;
    }
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/template-folders/${folder.id}`,
        "DELETE",
      );
      if (selectedFolderId === folder.id) {
        setSelectedFolderId(ALL);
      }
      await fetchFolders();
      await fetchOutreachTemplates(true);
      setAlert("Folder deleted", "success");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuFolder(null);
  };

  const renderChip = (
    selection: FolderSelection,
    label: string,
    folder?: TemplateFolder,
  ) => {
    const active = selectedFolderId === selection;
    return (
      <Box
        key={String(selection)}
        sx={styles.folderChip(active)}
        onClick={() => {
          setSelectedFolderId(active ? ALL : selection);
          trackEvent("Template Folder Selected", {
            "Template Folder ID":
              typeof selection === "number" ? selection : null,
          });
        }}
      >
        {folder && (
          <Box
            component="i"
            className="fa-solid fa-folder"
            sx={{ fontSize: 13, color: active ? "inherit" : "primary.main" }}
          />
        )}
        <Typography sx={styles.folderChipLabel}>{label}</Typography>
        <Typography sx={styles.folderChipCount}>
          {countFor(selection)}
        </Typography>
        {folder && (
          <IconButton
            size="small"
            sx={{ ml: -0.5, mr: -0.75, p: 0.25, color: "inherit" }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchorEl(e.currentTarget);
              setMenuFolder(folder);
            }}
          >
            <Box
              component="i"
              className="fa-solid fa-ellipsis"
              sx={{ fontSize: 12 }}
            />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Box sx={styles.folderRow}>
      {folders.map((folder) => renderChip(folder.id, folder.name, folder))}
      <Box component="button" sx={styles.newFolderChip} onClick={openCreate}>
        <Box
          component="i"
          className="fa-regular fa-plus"
          sx={{ fontSize: 12 }}
        />
        New folder
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuFolder) openRename(menuFolder);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-pen" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuFolder) handleDelete(menuFolder);
            closeMenu();
          }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <Box component="i" className="fa-regular fa-trash-can" />
          </ListItemIcon>
          <ListItemText
            sx={{ "& .MuiTypography-root": { color: "error.main" } }}
          >
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)}>
        <DialogTitle>
          {editingFolder ? "Rename folder" : "New folder"}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={saving || !name.trim()}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
